import React from "react";
import { Icon } from "@iconify/react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input, Label } from "../../../components/shared/InputField";
import { HStack, VStack } from "../../../components/utils";
import { ReportColumn } from "../utils/reportColumns";

type Props = {
  columns: ReportColumn[];
  selectedColumns: string[];
  customHeaders: Record<string, string>;
  onSelectedColumnsChange: (value: string[]) => void;
  onCustomHeadersChange: (value: Record<string, string>) => void;
  sortable?: boolean;
  footerContent?: React.ReactNode;
  orderSyncToken?: number;
};

type SortableColumnRowProps = {
  column: ReportColumn;
  checked: boolean;
  customHeaderValue: string;
  onToggle: (key: string) => void;
  onCustomHeaderChange: (key: string, value: string) => void;
  enableSorting: boolean;
};

const SortableColumnRow = ({
  column,
  checked,
  customHeaderValue,
  onToggle,
  onCustomHeaderChange,
  enableSorting,
}: SortableColumnRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key, disabled: !enableSorting });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <HStack
        key={column.key}
        className={`items-center gap-3 ${isDragging ? "opacity-60" : ""}`}
      >
        {enableSorting ? (
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
            aria-label={`Drag to reorder ${column.label}`}
          >
            <Icon icon="mdi:drag-vertical" width={18} />
          </button>
        ) : (
          <div className="w-[18px]" />
        )}
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(column.key)}
          className="accent-orange-501"
        />
        <div className="w-1/3 text-sm text-gray-700 truncate">
          {column.label}
        </div>
        <Input
          type="text"
          value={customHeaderValue}
          onChange={(e) => onCustomHeaderChange(column.key, e.target.value)}
          placeholder={column.label}
          disabled={!checked}
          className="flex-1"
        />
      </HStack>
    </div>
  );
};

const ColumnChooserSection = ({
  columns,
  selectedColumns,
  customHeaders,
  onSelectedColumnsChange,
  onCustomHeadersChange,
  sortable = false,
  footerContent,
  orderSyncToken,
}: Props) => {
  const [orderedColumns, setOrderedColumns] =
    React.useState<ReportColumn[]>(columns);
  const selectedColumnsRef = React.useRef<string[]>(selectedColumns);

  React.useEffect(() => {
    selectedColumnsRef.current = selectedColumns;
  }, [selectedColumns]);

  React.useEffect(() => {
    const columnMap = new Map(columns.map((column) => [column.key, column]));

    setOrderedColumns((previousOrderedColumns) => [
      ...previousOrderedColumns.filter((column) => columnMap.has(column.key)),
      ...columns.filter(
        (column) =>
          !previousOrderedColumns.some((prev) => prev.key === column.key)
      ),
    ]);
  }, [columns]);

  React.useEffect(() => {
    if (!sortable) return;

    const columnMap = new Map(columns.map((column) => [column.key, column]));
    setOrderedColumns((previousOrderedColumns) => {
      const selectedInOrder = selectedColumnsRef.current
        .filter((key) => columnMap.has(key))
        .map((key) => columnMap.get(key) as ReportColumn);

      const selectedSet = new Set(selectedColumnsRef.current);
      const restInCurrentOrder = previousOrderedColumns.filter(
        (column) => columnMap.has(column.key) && !selectedSet.has(column.key)
      );

      const missingColumns = columns.filter(
        (column) =>
          !selectedInOrder.some((sel) => sel.key === column.key) &&
          !restInCurrentOrder.some((rest) => rest.key === column.key)
      );

      return [...selectedInOrder, ...restInCurrentOrder, ...missingColumns];
    });
  }, [orderSyncToken, sortable, columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const isChecked = (key: string) => selectedColumns.includes(key);
  const orderedKeys = orderedColumns.map((column) => column.key);

  const toggleColumn = (key: string) => {
    if (isChecked(key)) {
      onSelectedColumnsChange(selectedColumns.filter((k) => k !== key));
      return;
    }
    const selectedSet = new Set(selectedColumns);
    selectedSet.add(key);
    onSelectedColumnsChange(
      orderedKeys.filter((columnKey) => selectedSet.has(columnKey))
    );
  };

  const updateCustomHeader = (key: string, value: string) => {
    onCustomHeadersChange({
      ...customHeaders,
      [key]: value,
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = orderedColumns.findIndex((c) => c.key === active.id);
    const newIndex = orderedColumns.findIndex((c) => c.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextOrdered = arrayMove(orderedColumns, oldIndex, newIndex);
    setOrderedColumns(nextOrdered);

    const selectedSet = new Set(selectedColumns);
    onSelectedColumnsChange(
      nextOrdered
        .map((column) => column.key)
        .filter((columnKey) => selectedSet.has(columnKey))
    );
  };

  return (
    <VStack className="w-full gap-3 mt-4 pt-4 border-t border-gray-100">
      <HStack className="items-center justify-between">
        <Label className="text-sm font-medium">Column Chooser</Label>
        <HStack className="gap-2">
          <button
            type="button"
            className="text-xs text-orange-600 hover:underline"
            onClick={() =>
              onSelectedColumnsChange(
                orderedColumns.map((column) => column.key)
              )
            }
          >
            Select all
          </button>
          <button
            type="button"
            className="text-xs text-gray-500 hover:underline"
            onClick={() => onSelectedColumnsChange([])}
          >
            Clear all
          </button>
        </HStack>
      </HStack>
      <div className="max-h-60 overflow-y-auto border border-gray-100 rounded-md p-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedColumns.map((column) => column.key)}
            strategy={verticalListSortingStrategy}
          >
            <VStack className="gap-3">
              {orderedColumns.map((column) => (
                <SortableColumnRow
                  key={column.key}
                  column={column}
                  checked={isChecked(column.key)}
                  customHeaderValue={customHeaders[column.key] || ""}
                  onToggle={toggleColumn}
                  onCustomHeaderChange={updateCustomHeader}
                  enableSorting={sortable}
                />
              ))}
            </VStack>
          </SortableContext>
        </DndContext>
      </div>
      <div className="text-xs text-gray-500">
        {sortable
          ? "Drag rows to reorder selected columns in the report. Leave header empty to keep default column name."
          : "Leave header empty to keep default column name."}
      </div>
      {footerContent}
    </VStack>
  );
};

export default ColumnChooserSection;
